import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const DEFAULT_AUCTION_CFG = {
  auction_type: "english", name: "", description: "", currency: "INR",
  start_at: "", end_at: "", timezone: "Asia/Kolkata (UTC+05:30)",
  starting_price: "", minimum_price: "", maximum_price: "",
  bid_decrement: "", bid_increment: "", round_interval_seconds: "60",
  extension_trigger_seconds: "", extension_duration_seconds: "",
  bid_visibility: "rank", supplier_company_ids: null, item_ids: null,
};

export const AUCTION_TYPE_LABELS = { english: "Reverse English", dutch: "Reverse Dutch", japanese: "Reverse Japanese" };

export function auctionPayload(cfg) {
  const num = (v) => (v === "" || v == null ? null : Number(v));
  return {
    auction_type: cfg.auction_type, name: cfg.name, description: cfg.description,
    currency: cfg.currency, timezone: cfg.timezone, bid_visibility: cfg.bid_visibility,
    start_at: new Date(cfg.start_at).toISOString(), end_at: cfg.end_at ? new Date(cfg.end_at).toISOString() : null,
    starting_price: Number(cfg.starting_price),
    minimum_price: num(cfg.minimum_price), maximum_price: num(cfg.maximum_price),
    bid_decrement: num(cfg.bid_decrement), bid_increment: num(cfg.bid_increment),
    round_interval_seconds: num(cfg.round_interval_seconds),
    extension_trigger_seconds: num(cfg.extension_trigger_seconds),
    extension_duration_seconds: num(cfg.extension_duration_seconds),
    ...(Array.isArray(cfg.supplier_company_ids) ? { supplier_company_ids: cfg.supplier_company_ids } : {}),
    ...(Array.isArray(cfg.item_ids) ? { item_ids: cfg.item_ids } : {}),
  };
}

export default function AuctionFields({ cfg, onChange, sellerOptions = null, itemOptions = null }) {
  const set = (k, v) => onChange({ ...cfg, [k]: v });
  const selSellers = Array.isArray(cfg.supplier_company_ids)
    ? cfg.supplier_company_ids
    : (sellerOptions || []).filter((s) => s.selectable).map((s) => s.id);
  const selItems = Array.isArray(cfg.item_ids) ? cfg.item_ids : (itemOptions || []).map((i) => i.id);
  const toggleIn = (k, id, resolved) => set(k, resolved.includes(id) ? resolved.filter((x) => x !== id) : [...resolved, id]);
  return (
    <div className="space-y-3" data-testid="auction-fields">
      <div>
        <Label>Auction Type *</Label>
        <div className="mt-1.5 grid grid-cols-3 gap-2">
          {Object.entries(AUCTION_TYPE_LABELS).map(([k, label]) => (
            <button key={k} type="button" data-testid={`auction-type-${k}`} onClick={() => set("auction_type", k)}
              className={`rounded-md border px-2 py-2 text-xs font-medium transition-colors ${cfg.auction_type === k ? "border-slate-900 bg-slate-900 text-white" : "hover:bg-slate-50"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {sellerOptions && (
        <div data-testid="auction-seller-selection">
          <Label>Select Sellers *</Label>
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid="seller-selection-count">{selSellers.length} / {sellerOptions.filter((s) => s.selectable).length} Sellers Selected</p>
          <div className="mt-1.5 max-h-40 space-y-1 overflow-y-auto rounded-md border p-2">
            {sellerOptions.map((s) => (
              <label key={s.id} className={`flex items-center gap-2.5 rounded px-2 py-1.5 text-sm ${s.selectable ? "cursor-pointer hover:bg-slate-50" : "opacity-60"}`}>
                <Checkbox data-testid={`select-seller-${s.name.replace(/\W+/g, "-").toLowerCase()}`}
                  disabled={!s.selectable} checked={selSellers.includes(s.id)}
                  onCheckedChange={() => s.selectable && toggleIn("supplier_company_ids", s.id, selSellers)} />
                <span className="font-medium">{s.name}</span>
                <span className={`ml-auto rounded-full border px-2 py-0.5 text-[10px] font-medium ${s.selectable ? "border-[#BBF7D0] bg-[#DCFCE7] text-[#166534]" : "border-[#FECACA] bg-[#FEE2E2] text-[#991B1B]"}`}>{s.statusLabel}</span>
              </label>
            ))}
          </div>
          {selSellers.length === 0 && <p className="mt-1 text-xs text-[#991B1B]" data-testid="seller-selection-error">Select at least one seller for this auction.</p>}
        </div>
      )}
      {itemOptions && (
        <div data-testid="auction-item-selection">
          <Label>Select Items for Auction *</Label>
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid="item-selection-count">{selItems.length} / {itemOptions.length} Items Selected</p>
          <div className="mt-1.5 max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
            {itemOptions.map((it) => (
              <label key={it.id} className="flex cursor-pointer items-start gap-2.5 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
                <Checkbox data-testid={`select-item-${it.material_code}`} className="mt-0.5"
                  checked={selItems.includes(it.id)} onCheckedChange={() => toggleIn("item_ids", it.id, selItems)} />
                <span>
                  <span className="font-mono text-xs font-semibold">Line {it.line} — {it.material_code}</span>
                  <span className="block text-xs text-muted-foreground">{it.description} · {it.quantity} {it.uom}</span>
                </span>
              </label>
            ))}
          </div>
          {selItems.length === 0 && <p className="mt-1 text-xs text-[#991B1B]" data-testid="item-selection-error">Please select at least one Event Item for this auction.</p>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Auction Name</Label><Input data-testid="auction-name" className="mt-1" placeholder="Defaults to event number" value={cfg.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div><Label className="text-xs">Currency</Label><Input data-testid="auction-currency" className="mt-1" value={cfg.currency} onChange={(e) => set("currency", e.target.value)} /></div>
      </div>
      <div><Label className="text-xs">Description</Label><Textarea data-testid="auction-description" rows={1} className="mt-1" value={cfg.description} onChange={(e) => set("description", e.target.value)} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Start *</Label><Input data-testid="auction-start" required type="datetime-local" className="mt-1" value={cfg.start_at} onChange={(e) => set("start_at", e.target.value)} /></div>
        {cfg.auction_type === "japanese" ? (
          <div><Label className="text-xs">End</Label><p className="mt-2 text-xs text-muted-foreground" data-testid="japanese-no-end-note">No fixed end — the auction runs until all sellers exit or the price reaches ₹0.</p></div>
        ) : (
          <div><Label className="text-xs">End <span className="font-normal text-muted-foreground">(optional — defaults to 24h after start)</span></Label><Input data-testid="auction-end" type="datetime-local" className="mt-1" value={cfg.end_at} onChange={(e) => set("end_at", e.target.value)} /></div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">{cfg.auction_type === "dutch" ? "Starting (Floor) Price *" : "Starting / Ceiling Price *"}</Label><Input data-testid="auction-starting-price" required type="number" min="1" className="mt-1" value={cfg.starting_price} onChange={(e) => set("starting_price", e.target.value)} /></div>
        <div><Label className="text-xs">Timezone</Label><Input data-testid="auction-timezone" className="mt-1" value={cfg.timezone} onChange={(e) => set("timezone", e.target.value)} /></div>
      </div>
      {cfg.auction_type === "english" && (
        <div className="grid grid-cols-2 gap-3" data-testid="english-fields">
          <div><Label className="text-xs">Minimum Bid Decrement *</Label><Input data-testid="auction-decrement" required type="number" min="1" className="mt-1" value={cfg.bid_decrement} onChange={(e) => set("bid_decrement", e.target.value)} /></div>
          <div><Label className="text-xs">Minimum Price (optional)</Label><Input data-testid="auction-min-price" type="number" min="0" className="mt-1" value={cfg.minimum_price} onChange={(e) => set("minimum_price", e.target.value)} /></div>
          <div><Label className="text-xs">Anti-snipe Trigger (sec, optional)</Label><Input data-testid="auction-ext-trigger" type="number" min="0" className="mt-1" value={cfg.extension_trigger_seconds} onChange={(e) => set("extension_trigger_seconds", e.target.value)} /></div>
          <div><Label className="text-xs">Extension Duration (sec, optional)</Label><Input data-testid="auction-ext-duration" type="number" min="0" className="mt-1" value={cfg.extension_duration_seconds} onChange={(e) => set("extension_duration_seconds", e.target.value)} /></div>
        </div>
      )}
      {cfg.auction_type === "dutch" && (
        <div className="grid grid-cols-3 gap-3" data-testid="dutch-fields">
          <div><Label className="text-xs">Price Increment *</Label><Input data-testid="auction-increment" required type="number" min="1" className="mt-1" value={cfg.bid_increment} onChange={(e) => set("bid_increment", e.target.value)} /></div>
          <div><Label className="text-xs">Interval (sec) *</Label><Input data-testid="auction-interval" required type="number" min="5" className="mt-1" value={cfg.round_interval_seconds} onChange={(e) => set("round_interval_seconds", e.target.value)} /></div>
          <div><Label className="text-xs">Maximum Price *</Label><Input data-testid="auction-max-price" required type="number" min="1" className="mt-1" value={cfg.maximum_price} onChange={(e) => set("maximum_price", e.target.value)} /></div>
        </div>
      )}
      {cfg.auction_type === "japanese" && (
        <div className="grid grid-cols-2 gap-3" data-testid="japanese-fields">
          <div><Label className="text-xs">Price Decrement *</Label><Input data-testid="auction-japanese-decrement" required type="number" min="1" className="mt-1" value={cfg.bid_decrement} onChange={(e) => set("bid_decrement", e.target.value)} /></div>
          <div><Label className="text-xs">Round Interval (sec) *</Label><Input data-testid="auction-japanese-interval" required type="number" min="5" className="mt-1" value={cfg.round_interval_seconds} onChange={(e) => set("round_interval_seconds", e.target.value)} /></div>
        </div>
      )}
      <div>
        <Label className="text-xs">Bid Visibility (what sellers see)</Label>
        <Select value={cfg.bid_visibility} onValueChange={(v) => set("bid_visibility", v)}>
          <SelectTrigger data-testid="auction-visibility" className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="rank">Rank Only</SelectItem>
            <SelectItem value="price">Current Best Price</SelectItem>
            <SelectItem value="rank_price">Rank + Best Price</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
